import React, { useState, useEffect } from 'react';
import { getContacts } from '../../services/zohoApi';

const ContactManagement = () => {
    const [vendors, setVendors] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');
    const [activeTab, setActiveTab] = useState('vendors');

    useEffect(() => {
        loadContacts();
    }, []);

    const loadContacts = async () => {
        setIsLoading(true);
        setError('');
        try {
            const [vendorList, customerList] = await Promise.all([
                getContacts({ contact_type: 'vendor' }),
                getContacts({ contact_type: 'customer' })
            ]);
            
            setVendors(vendorList || []);
            setCustomers(customerList || []);
        } catch (err) {
            setError('Error loading contacts: ' + err.message);
        } finally {
            setIsLoading(false);
        }
    };

    const currentContacts = activeTab === 'vendors' ? vendors : customers;

    return (
        <div className="contact-management-section">
            <div className="section-header">
                <h2>Contact Viewer</h2>
                <p>View vendors and customers from Zoho Inventory</p>
            </div>

            {error && (
                <div className="error-message">
                    <strong>Error:</strong> {error}
                </div>
            )}

            {/* Tab Navigation */}
            <div className="tab-navigation">
                <button 
                    className={`tab-button ${activeTab === 'vendors' ? 'active' : ''}`}
                    onClick={() => setActiveTab('vendors')}
                >
                    Vendors ({vendors.length})
                </button>
                <button 
                    className={`tab-button ${activeTab === 'customers' ? 'active' : ''}`}
                    onClick={() => setActiveTab('customers')}
                >
                    Customers ({customers.length})
                </button>
            </div>

            {/* Refresh Button Only */}
            <div className="actions-row">
                <div style={{ flex: 1 }}>
                    <span style={{ color: '#666', fontSize: '14px' }}>
                        📋 View-only mode - Contacts are managed directly in Zoho Inventory
                    </span>
                </div>
                <button 
                    className="btn btn-secondary"
                    onClick={loadContacts}
                    disabled={isLoading}
                >
                    🔄 Refresh from Zoho
                </button>
            </div>

            {/* Contacts List */}
            <div className="contacts-list">
                {isLoading && <div className="loading">Loading contacts...</div>}
                
                {!isLoading && currentContacts.length === 0 && (
                    <div className="empty-state">
                        <p>No {activeTab} found in Zoho Inventory.</p>
                        <p style={{ fontSize: '14px', color: '#999' }}>
                            Create contacts directly in Zoho Inventory, then refresh here to view them.
                        </p>
                    </div>
                )}

                {!isLoading && currentContacts.length > 0 && (
                    <div className="contacts-table-container">
                        <table className="contacts-table">
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th>Email</th>
                                    <th>Phone</th>
                                    <th>Website</th>
                                    <th>Status</th>
                                    <th>Info</th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentContacts.map((contact) => (
                                    <tr key={contact.contact_id}>
                                        <td>
                                            <strong>{contact.contact_name}</strong>
                                            {contact.company_name && contact.company_name !== contact.contact_name && (
                                                <div className="company-name">{contact.company_name}</div>
                                            )}
                                        </td>
                                        <td>{contact.email || '-'}</td>
                                        <td>{contact.phone || '-'}</td>
                                        <td>
                                            {contact.website ? (
                                                <a href={contact.website} target="_blank" rel="noopener noreferrer">
                                                    View
                                                </a>
                                            ) : '-'}
                                        </td>
                                        <td>
                                            <span className={`status-badge ${contact.status}`}>
                                                {contact.status}
                                            </span>
                                        </td>
                                        <td>
                                            <span style={{ 
                                                color: '#999', 
                                                fontSize: '12px',
                                                fontStyle: 'italic' 
                                            }}>
                                                ID: {contact.contact_id}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ContactManagement;
